import React, { useState, useMemo } from 'react';
import { IconButton, Menu, MenuItem, Box, useTheme, Grid, Typography, Tooltip, Button } from '@mui/material';
import { MoreVert, Visibility, Share, GetApp, ChevronRight } from '@mui/icons-material';
import { Hierarchy, OrderAndEye } from 'ui-component/icons';
import { IconPin } from '@tabler/icons';
import { GridRenderCellParams } from '@mui/x-data-grid-pro';
import { Popover } from 'ui-component/customFormsComponents/HierarchySection/components/HierarchyGridPopover/components';
import { HierarchyDialog } from 'ui-component/customFormsComponents/HierarchySection/HierarchyDialog';
import { DataFocusViewDialog } from 'ui-component/dialogs';
import { IRecordHeaders } from 'ui-component/records/types';
import { usePinnedActions } from '../PinnedActionsContext';
import { DataFocusViewButtonMenu, DFV_MENU_PREF_KEY } from '../DataFocusViewButton/components';
import { RecordGridActionsMenuItem, RecordGridActionsAction } from './types';
import { RenderPinnedMenuItem, TooltipTitle } from './components';
import { RECORD_VIEW_MENU_PREF_KEY, RecordViewButtonMenu } from '../RecordViewButton/components';
import { RecordViewDialog } from 'ui-component/RecordView';
import { openInANewTabRecordViewAction, openInThisTabRecordViewAction } from '../RecordViewButton/utils';
import { openInANewTabDFVAction, openInThisTabDFVAction } from '../DataFocusViewButton/utils';
import { DATA_FOCUS_VIEW_PREF, DataFocusViewPref, RECORD_VIEW_PREF, RecordViewPref } from 'hooks/useLocalStorageListener';
import { openSnackbar } from 'store/slices/snackbar';
import { useDispatch } from 'store';
import { IRecordType } from 'types/recordType';

export type RecordGridActionsColumnProps = {
    params: GridRenderCellParams;
    recordTypeId: number;
    onViewRecord: (recordId: string, recordType: IRecordType, type?: 'document' | 'recordDetails' | 'dynamicDataFocusView') => void;
    onRecordShareURL: (recordId: string, type: string) => void;
    onDownloadFile: (fileId: string) => void;
    canViewRecordViewer?: boolean;
    isAllowedDataFocusView?: boolean;
    isAllowedDynamicDataFocusView?: boolean;
    hasRecordFile?: boolean;
    onUpdate: (updatedRecord: IRecordHeaders) => void;
};

export const RecordGridActionsColumn = ({
    params,
    recordTypeId,
    onViewRecord,
    onRecordShareURL,
    onDownloadFile,
    canViewRecordViewer = false,
    isAllowedDataFocusView = false,
    isAllowedDynamicDataFocusView = false,
    hasRecordFile = false,
    onUpdate
}: RecordGridActionsColumnProps) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [optionAnchorEl, setOptionAnchorEl] = useState<null | HTMLElement>(null);
    const [isOpenFullHierarchy, setIsOpenFullHierarchy] = useState(false);
    const [isOpenDataFocusViewDialog, setIsOpenDataFocusViewDialog] = useState(false);
    const [isOpenRecordViewDialog, setIsOpenRecordViewDialog] = useState(false);
    const [selectedSecondLevel, setSelectedSecondLevel] = useState<'HIERARCHY' | 'RECORD_VIEW' | 'DYNAMIC_DATA_FOCUS_VIEW' | null>(null);
    const open = Boolean(anchorEl);

    const { pinnedActions, pendingPinnedActions, togglePinAction, canPinMore } = usePinnedActions();

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleAction = (action: RecordGridActionsAction) => (e: React.MouseEvent<HTMLLIElement>) => {
        if (!(['HIERARCHY', 'RECORD_VIEW', 'DYNAMIC_DATA_FOCUS_VIEW'] as RecordGridActionsAction[]).includes(action)) {
            handleClose();
        }

        switch (action) {
            case 'DATA_FOCUS_VIEW':
                if (isAllowedDataFocusView) {
                    onViewRecord(String(params.id), params.row.recordType, 'recordDetails');
                }
                break;
            case 'DYNAMIC_DATA_FOCUS_VIEW':
                if (isAllowedDynamicDataFocusView) {
                    const pref = localStorage.getItem(DFV_MENU_PREF_KEY) as DataFocusViewPref | null;
                    if (pref && Object.values(DATA_FOCUS_VIEW_PREF).includes(pref)) {
                        if (pref === 'OPEN_IN_THIS_TAB') {
                            openInThisTabDFVAction(String(params.id), params.row.recordType);
                        }
                        if (pref === 'OPEN_IN_A_NEW_TAB') {
                            openInANewTabDFVAction(String(params.id), params.row.recordType);
                        }
                        if (pref === 'OPEN_IN_A_POP_UP_WINDOW') {
                            setIsOpenDataFocusViewDialog(true);
                        }
                    }
                }
                break;
            case 'RECORD_VIEW':
                if (canViewRecordViewer) {
                    const pref = localStorage.getItem(RECORD_VIEW_MENU_PREF_KEY) as RecordViewPref | null;
                    if (pref && Object.values(RECORD_VIEW_PREF).includes(pref)) {
                        if (pref === 'OPEN_IN_THIS_TAB') {
                            openInThisTabRecordViewAction(String(params.id), params.row.recordType);
                        }
                        if (pref === 'OPEN_IN_A_NEW_TAB') {
                            openInANewTabRecordViewAction(String(params.id), params.row.recordType);
                        }
                        if (pref === 'OPEN_IN_A_POP_UP_WINDOW') {
                            setIsOpenRecordViewDialog(true);
                        }
                    }
                }
                break;
            case 'SHARE':
                onRecordShareURL(String(params.id), params.row.recordType);
                break;
            case 'DOWNLOAD':
                if (hasRecordFile && params.row.recordFile?.id) {
                    onDownloadFile(String(params.row.recordFile.id));
                }
                break;

            case 'HIERARCHY':
                if (params.row.children?.length || params.row.parent) {
                    setOptionAnchorEl(e.currentTarget);
                    setSelectedSecondLevel('HIERARCHY');
                }
                break;
            default:
                break;
        }
    };

    const handlePinAction = (actionId: string) => {
        togglePinAction(actionId);
    };

    const handleUpdateRecord = (updatedRecord: IRecordHeaders) => {
        dispatch(
            openSnackbar({
                open: true,
                message: `Update successful`,
                variant: 'alert',
                alert: {
                    color: 'success'
                },
                close: true
            })
        );
        onUpdate(updatedRecord);
    };

    const handleClickMore =
        (actionId: 'HIERARCHY' | 'RECORD_VIEW' | 'DYNAMIC_DATA_FOCUS_VIEW') => (e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            setSelectedSecondLevel(actionId);
            setOptionAnchorEl(e.currentTarget);
        };

    const menuItems: RecordGridActionsMenuItem[] = [
        {
            id: 'DYNAMIC_DATA_FOCUS_VIEW',
            label: 'Dynamic Data Focus View',
            description: 'View the customer request and its process flow',
            icon: <OrderAndEye fontSize="small" />,
            show: isAllowedDynamicDataFocusView,
            hasArrow: true
        },
        {
            id: 'RECORD_VIEW',
            label: 'Record View',
            description: 'Open the full details of this record',
            icon: <Visibility fontSize="small" />,
            show: canViewRecordViewer,
            hasArrow: true
        },
        {
            id: 'DATA_FOCUS_VIEW',
            label: 'Data Focus View',
            description: 'View the customer request and its process flow',
            icon: <OrderAndEye fontSize="small" />,
            show: isAllowedDataFocusView,
            hasArrow: false
        },
        {
            id: 'HIERARCHY',
            label: 'Hierarchy',
            description:
                !params.row.children?.length && !params.row.parent
                    ? 'This record does not have a parent or child'
                    : 'This record have a parent or child',
            icon: <Hierarchy sx={{ fontSize: '20px' }} />,
            show: true,
            hasArrow: false,
            disabled: !params.row.children?.length && !params.row.parent
        },
        {
            id: 'SHARE',
            label: 'Share',
            description: 'Get shareable link',
            icon: <Share fontSize="small" />,
            show: true,
            hasArrow: false
        },
        {
            id: 'DOWNLOAD',
            label: 'Download',
            description: 'Download the record file',
            icon: <GetApp fontSize="small" />,
            show: hasRecordFile && params.row.recordFile?.id,
            hasArrow: false
        }
        // {
        //     id: 'duplicate',
        //     label: 'Duplicate',
        //     description: 'Create a copy of this record',
        //     icon: <ContentCopy fontSize="small" />,
        //     show: false,
        //     hasArrow: false
        // },
        // {
        //     id: 'delete',
        //     label: 'Soft Delete',
        //     description: 'Mark this record as deleted without erasing it.',
        //     icon: <Delete fontSize="small" />,
        //     show: false,
        //     hasArrow: false
        // }
    ].filter((item) => item.show) as RecordGridActionsMenuItem[];

    // Usar pinnedActions (estado real) para mostrar los icon buttons
    const pinnedMenuItems = useMemo(() => {
        const filtered = menuItems.filter((item) => pinnedActions.includes(item.id));
        return filtered;
    }, [menuItems, pinnedActions]);

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                width: '100%',
                justifyContent: pinnedMenuItems.length ? 'end' : 'center'
            }}
        >
            {pinnedMenuItems.map((item) => (
                <RenderPinnedMenuItem
                    key={item.id}
                    canPinMore={canPinMore}
                    item={item}
                    hasChildrenOrParent={params.row.children?.length || params.row.parent}
                    onAction={handleAction}
                    pinnedActions={pinnedActions}
                    record={params.row}
                    onUpdate={handleUpdateRecord}
                />
            ))}
            <IconButton
                size="small"
                onClick={handleClick}
                aria-controls={open ? 'record-actions-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
            >
                <MoreVert fontSize="small" />
            </IconButton>
            <Menu
                id="record-actions-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'record-actions-button'
                }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{
                    sx: {
                        bgcolor: `${theme.palette.primary[300]} !important`,
                        border: `solid 1px ${theme.palette.primary[500]} !important`,
                        borderRadius: '8px',
                        minWidth: '347px',
                        '& ul': { py: 0 }
                    }
                }}
            >
                {menuItems.map((item) => (
                    <Tooltip
                        placement="right"
                        arrow
                        title={<TooltipTitle label={item.label} description={item.description} />}
                        key={item.id}
                        sx={{
                            '& span:last-child li': { borderBottom: 'none !important' }
                        }}
                    >
                        <span>
                            <MenuItem
                                onClick={handleAction(item.id)}
                                disabled={item.disabled}
                                sx={{
                                    height: '32px',
                                    py: 0,
                                    borderBottom: `solid 1px ${theme.palette.primary[500]} !important`,
                                    '&:hover': {
                                        bgcolor: theme.palette.secondary.light
                                    }
                                }}
                            >
                                <Grid container alignItems="center" sx={{ height: '100%' }}>
                                    <Grid item xs>
                                        <Typography fontSize="14px" fontWeight={400} color="#54595E">
                                            {item.label}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs="auto">
                                        <IconButton sx={{ py: 0 }} disableFocusRipple disableRipple disableTouchRipple>
                                            {item.icon}
                                        </IconButton>
                                    </Grid>
                                    <Grid item xs="auto">
                                        <Tooltip
                                            placement="right"
                                            arrow
                                            title={
                                                !canPinMore && !pendingPinnedActions.includes(item.id) ? (
                                                    <TooltipTitle label="Pin" description="Limit: only 2 buttons can be pinned" />
                                                ) : (
                                                    ''
                                                )
                                            }
                                            disableHoverListener={!(!canPinMore && !pendingPinnedActions.includes(item.id))}
                                        >
                                            <span>
                                                <Button
                                                    sx={{
                                                        borderRadius: '8px',
                                                        width: 20,
                                                        height: 20,
                                                        minWidth: 20,
                                                        p: 0,
                                                        boxShadow: 'none',
                                                        bgcolor: pendingPinnedActions.includes(item.id)
                                                            ? theme.palette.primary.main
                                                            : '#FFFFFF',
                                                        '&:hover svg': { stroke: '#FFFFFF !important' }
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handlePinAction(item.id);
                                                    }}
                                                    variant="contained"
                                                    disableRipple
                                                    disableFocusRipple
                                                    disableTouchRipple
                                                    disableElevation
                                                    disabled={!canPinMore && !pendingPinnedActions.includes(item.id)}
                                                >
                                                    <IconPin
                                                        size={16}
                                                        color={pendingPinnedActions.includes(item.id) ? '#FFFFFF' : '#37326E'}
                                                    />
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    </Grid>
                                    <Grid item xs="auto" sx={{ width: '24px !important', mr: '-5px' }}>
                                        {item.hasArrow && (
                                            <IconButton
                                                sx={{ py: 0 }}
                                                onClick={handleClickMore(
                                                    item.id as 'HIERARCHY' | 'RECORD_VIEW' | 'DYNAMIC_DATA_FOCUS_VIEW'
                                                )}
                                                disableFocusRipple
                                                disableRipple
                                                disableTouchRipple
                                            >
                                                <ChevronRight />
                                            </IconButton>
                                        )}
                                    </Grid>
                                </Grid>
                            </MenuItem>
                        </span>
                    </Tooltip>
                ))}
            </Menu>

            {optionAnchorEl && selectedSecondLevel === 'HIERARCHY' && (
                <>
                    <Popover
                        open={!!optionAnchorEl}
                        anchorEl={optionAnchorEl}
                        record={params.row}
                        onViewFullHierarchy={() => setIsOpenFullHierarchy(true)}
                        onClose={() => {
                            setOptionAnchorEl(null);
                            setSelectedSecondLevel(null);
                            handleClose();
                        }}
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    />
                    {isOpenFullHierarchy && (
                        <HierarchyDialog
                            open={isOpenFullHierarchy}
                            onClose={() => setIsOpenFullHierarchy(false)}
                            recordId={params.row.id}
                            initialRecord={params.row}
                        />
                    )}
                </>
            )}

            {optionAnchorEl && selectedSecondLevel === 'RECORD_VIEW' && (
                <RecordViewButtonMenu
                    open={!!optionAnchorEl}
                    anchorEl={optionAnchorEl}
                    onClose={() => {
                        setOptionAnchorEl(null);
                        setSelectedSecondLevel(null);
                        handleClose();
                    }}
                    recordId={params.row.id}
                    recordType={params.row.recordType}
                    onOpenInAPopUp={() => setIsOpenRecordViewDialog(true)}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                />
            )}

            {optionAnchorEl && selectedSecondLevel === 'DYNAMIC_DATA_FOCUS_VIEW' && (
                <DataFocusViewButtonMenu
                    open={!!optionAnchorEl}
                    anchorEl={optionAnchorEl}
                    onClose={() => {
                        setOptionAnchorEl(null);
                        setSelectedSecondLevel(null);
                        handleClose();
                    }}
                    recordId={params.row.id}
                    recordType={params.row.recordType}
                    onOpenInAPopUp={() => setIsOpenDataFocusViewDialog(true)}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                />
            )}

            {isOpenRecordViewDialog && (
                <RecordViewDialog
                    recordData={{ id: params.row.id, recordType: params.row.recordType }}
                    open={isOpenRecordViewDialog}
                    onClose={(success?: boolean, recordData?: IRecordHeaders) => {
                        setOptionAnchorEl(null);
                        setSelectedSecondLevel(null);
                        setIsOpenRecordViewDialog(false);
                        if (success && recordData) onUpdate(recordData);
                        handleClose();
                    }}
                />
            )}

            {isOpenDataFocusViewDialog && (
                <DataFocusViewDialog
                    recordId={+params.row.id}
                    recordType={params.row.recordType}
                    open={isOpenDataFocusViewDialog}
                    onClose={(updatedRecord) => {
                        setOptionAnchorEl(null);
                        setSelectedSecondLevel(null);
                        setIsOpenDataFocusViewDialog(false);
                        if (updatedRecord) onUpdate(updatedRecord);
                        handleClose();
                    }}
                    showCloseWarning
                />
            )}
        </Box>
    );
};
